import useMeasure from '@/hooks/useMeasure/useMeasure';
import { animated, config, useSpring } from '@react-spring/web';
import clsx from 'clsx';
import { ChangeEvent, useRef, useState } from 'react';
import ToggleIcon from '../ToggleIcon/ToggleIcon';
import styles from './FormToggleCard.module.scss';
import type { FormToggleCardProps } from './types';

const FormToggleCard = ({
  children,
  onChange,
  label = 'test',
  defaultChecked,
  checked,
  disabled,
  id,
  name,
}: FormToggleCardProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(defaultChecked ?? false);
  const childrenWrapperRef = useRef<HTMLDivElement>(null);
  const { height } = useMeasure(childrenWrapperRef, true);
  const isControlled = typeof checked !== 'undefined';

  const springProps = useSpring({
    maxHeight: (isControlled ? checked : isOpen) ? `${height}px` : '0px',
    config: config.default,
  } as const);

  const handleOnChange = ($event: ChangeEvent<HTMLInputElement>) => {
    setIsOpen(() => {
      const newValue = $event.target.checked;
      onChange?.($event);
      return newValue;
    });
  };

  const checkBoxProps = isControlled
    ? { onChange, checked }
    : { onChange: handleOnChange, checked: isOpen };

  return (
    <div className={styles.base}>
      <div className={styles.header}>
        <ToggleIcon
          id={id}
          name={name}
          disabled={disabled}
          {...checkBoxProps}
        />
        <span
          className={clsx(styles.label, { [styles.active]: checked || isOpen })}
        >
          {label}
        </span>
      </div>

      <animated.div style={springProps} className={styles.animatedWrapper}>
        <div ref={childrenWrapperRef}>
          <div className={styles.childrenWrapper}>{children}</div>
        </div>
      </animated.div>
    </div>
  );
};

export default FormToggleCard;
